import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import {
    CButton,
    CForm,
    CFormInput,
    CFormLabel,
    CFormFeedback,
    CFormText,
    CSpinner,
} from '@coreui/react';

import * as actions from '../../store/actions/index';
import logo from '../../assets/Images/White_Logo.svg';

const RESEND_COOLDOWN_SECONDS = 60;

const ForgotPassword = (props) => {
    const {
        onforgotpass,
        loading: reduxLoading,
    } = props;

    const [EmailAddress, setEmailAddress] = useState('');
    const [validated, setValidated] = useState(false);
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [hasSentOnce, setHasSentOnce] = useState(false);
    const [sentToEmail, setSentToEmail] = useState('');
    const [cooldown, setCooldown] = useState(0);

    const cooldownTimer = useRef(null);

    // Cleanup cooldown timer when component unmounts
    useEffect(() => {
        return () => {
            clearInterval(cooldownTimer.current);
        };
    }, []);

    const startCooldown = () => {
        clearInterval(cooldownTimer.current);

        setCooldown(RESEND_COOLDOWN_SECONDS);

        cooldownTimer.current = setInterval(() => {
            setCooldown((prev) => {
                if (prev <= 1) {
                    clearInterval(cooldownTimer.current);
                    return 0;
                }

                return prev - 1;
            });
        }, 1000);
    };

    const sendResetEmail = async (email) => {
        setLoading(true);
        setShowError(false);
        setErrorMessage('');

        try {
            const response = await onforgotpass(email);

            if (response.type === 'AUTH_SUCCESS') {
                setHasSentOnce(true);
                setSentToEmail(email);
                setLoading(false);

                startCooldown();
            } else {
                setShowError(true);
                setErrorMessage(
                    response.data || 'An error occurred, please try again.'
                );
                setLoading(false);

                setTimeout(() => {
                    setShowError(false);
                    setErrorMessage('');
                }, 5000);
            }
        } catch (error) {
            setShowError(true);
            setErrorMessage(
                'Something went wrong. Please try again later.'
            );
            setLoading(false);

            setTimeout(() => {
                setShowError(false);
                setErrorMessage('');
            }, 5000);
        }
    };

    const handleSubmit = (event) => {
        event.preventDefault();

        const form = event.currentTarget;

        if (form.checkValidity() === true) {
            sendResetEmail(EmailAddress);
        } else {
            setValidated(true);
        }
    };

    const handleResend = () => {
        sendResetEmail(sentToEmail);
    };

    const handleChange = (event) => {
        setEmailAddress(event.target.value);
    };

    const renderRequestForm = () => {
        const loadingState = reduxLoading || loading;

        return (
            <>
                <h2 className="forgot-password__title">
                    Forgot password?
                </h2>

                <p className="forgot-password__description">
                    Enter the email address associated with your account and
                    we&rsquo;ll send you a link to reset your password.
                </p>

                {showError && (
                    <div className="forgot-password__alert forgot-password__alert--error">
                        {errorMessage}
                    </div>
                )}

                <CForm
                    noValidate
                    validated={validated}
                    onSubmit={handleSubmit}
                >
                    <CFormLabel htmlFor="EmailAddress">
                        Email Address
                    </CFormLabel>

                    <CFormInput
                        id="EmailAddress"
                        type="email"
                        placeholder="e.g you@company.com"
                        value={EmailAddress}
                        onChange={handleChange}
                        required
                    />

                    <CFormFeedback invalid>
                        Please enter a valid email.
                    </CFormFeedback>

                    <CFormText>
                        Check spelling before submitting.
                    </CFormText>

                    <CButton
                        type="submit"
                        className="forgot-password__submit"
                       disabled={loadingState || !EmailAddress.trim()}
                    >
                        {loadingState ? (
                            <CSpinner size="sm" />
                        ) : (
                            'Send link to email'
                        )}
                    </CButton>

                    <CButton
                        type="button"
                        className="forgot-password__resend"
                        disabled
                    >
                        Resend email
                    </CButton>

                    <a
                        href="/login"
                        className="forgot-password__back"
                    >
                        Back to login
                    </a>
                </CForm>
            </>
        );
    };

    const renderSentConfirmation = () => {
        const loadingState = reduxLoading || loading;
        const resendDisabled = loadingState || cooldown > 0;

        return (
            <>
                <div className="forgot-password__alert forgot-password__alert--success">
                    <span className="forgot-password__alert-icon">
                        &#10003;
                    </span>

                    Reset email successfully sent
                </div>

                <h2 className="forgot-password__title">
                    Check your inbox
                </h2>

                <p className="forgot-password__description">
                    We&rsquo;ve sent a temporary password recovery link to{' '}
                    <strong>{sentToEmail}</strong>. Please click the link inside
                    to set a secure password.
                </p>

                {showError && (
                    <div className="forgot-password__alert forgot-password__alert--error">
                        {errorMessage}
                    </div>
                )}

                <p className="forgot-password__hint">
                    Don&rsquo;t see it? Make sure to check your spam folder, or
                    wait for the cooldown timer to expire.
                </p>

                <CButton
                    type="button"
                    className="forgot-password__submit"
                    href="/login"
                >
                    Back to log in
                </CButton>

                <CButton
                    type="button"
                    className="forgot-password__resend"
                    disabled={resendDisabled}
                    onClick={handleResend}
                >
                    {loadingState ? (
                        <CSpinner size="sm" />
                    ) : (
                        'Resend email'
                    )}
                </CButton>

                <p className="forgot-password__cooldown">
                    {cooldown > 0
                        ? `You can request a new link in ${cooldown}s`
                        : 'You can request a new link now'}
                </p>
            </>
        );
    };

    return (
        <div className="forgot-password">
           <div className="forgot-password__brand">
            <div className="forgot-password__brand-inner">
                <div className="forgot-password__logo">
                    <img src={logo} alt="Quick Query" />
                </div>

                <h1 className="forgot-password__headline">
                    Automate your account mapping in minutes
                </h1>

                <p className="forgot-password__subtext">
                    Configure standard expense categories, run clean
                    multi-factor compliance reporting, and synchronize
                    seamlessly to your general ledger.
                </p>
            </div>
        </div>
            <div className="forgot-password__panel">
                <div className="forgot-password__card">
                    {hasSentOnce
                        ? renderSentConfirmation()
                        : renderRequestForm()}
                </div>
            </div>
        </div>
    );
};

const mapStateToProps = (state) => {
    return {
        loading: state.auth.loading,
        error: state.auth.error,
        isAuthenticated: state.auth._id !== null,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        onforgotpass: (EmailAddress) => {
            return new Promise((resolve, reject) => {
                dispatch(actions.userForgot(EmailAddress))
                    .then((response) => resolve(response))
                    .catch((error) => reject(error));
            });
        },
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(ForgotPassword);